import { useHead } from '@unhead/vue'
import { toValue, type MaybeRefOrGetter } from 'vue'
import { useI18n } from 'vue-i18n'
import { SITE_URL } from './useSeo'

/**
 * Build an absolute, locale-prefixed URL from an UNPREFIXED path suffix.
 *
 * The homepage suffix '/' collapses to no trailing slash, because vue-router
 * emits '/de' rather than '/de/'. A trailing slash would make the canonical
 * disagree with the URL the locale redirect actually lands on, which is the
 * one thing a canonical must never do.
 */
export function localeUrl(locale: string, suffix: string): string {
  return `${SITE_URL}/${locale}${suffix === '/' ? '' : suffix}`
}

/**
 * Locale-bound URL builder for structured data. JSON-LD is serialised once at
 * setup, so it needs a plain string rather than the reactive getter that the
 * head tags take.
 */
export function useLocaleUrl(): (suffix: string) => string {
  // useI18n() rather than a module-scope locale accessor: those are client-only
  // by contract and return null during SSR, which is the pass crawlers read.
  const { locale } = useI18n()
  return (suffix: string) => localeUrl(locale.value, suffix)
}

/**
 * Canonical URL for the active locale, plus the hreflang alternates.
 *
 * Takes the UNPREFIXED suffix ('/products/kubernetes'), never the live route
 * path: once the router carries the '/:locale' segment, prefixing route.path
 * would emit '/de/de/products/kubernetes' on every page.
 */
/** Per-locale suffixes, for pages whose URL differs between languages. */
export interface LocaleSuffixes {
  de?: string
  en?: string
}

export function useCanonical(
  suffix: MaybeRefOrGetter<string> | LocaleSuffixes
): () => string {
  const { locale } = useI18n()

  // A blog post can live at a different slug per language, and may exist in
  // only one. Emitting one shared suffix as both alternates points hreflang at
  // a URL that 404s, which is how the renamed English posts ended up
  // advertising German URLs that never existed.
  // A function is never typeof 'object', so no need to exclude it here.
  const perLocale = typeof suffix === 'object' && !('value' in suffix)
  const suffixFor = (l: 'de' | 'en'): string | undefined =>
    perLocale ? (suffix as LocaleSuffixes)[l] : toValue(suffix as MaybeRefOrGetter<string>)

  const alternates = (['de', 'en'] as const)
    .filter((l) => suffixFor(l) !== undefined)
    .map((l) => ({
      rel: 'alternate' as const,
      hreflang: l,
      href: () => localeUrl(l, suffixFor(l) as string)
    }))

  // No '/' fallback: emitting the homepage as a post's canonical is the kind
  // of error that hides rather than crashes. The active locale is always
  // present here, because the post was looked up by it in the first place.
  const ownSuffix = () => suffixFor(locale.value as 'de' | 'en') ?? suffixFor('de') ?? ''

  useHead({
    link: [
      ...alternates,
      // x-default ONLY on the homepage, matching scripts/generate-sitemap.mjs
      // so the two signals cannot be read as contradicting each other.
      // It marks the language-neutral entry point, and the bare root is the
      // only URL that negotiates: /agb and friends hard-redirect to /de
      // regardless of Accept-Language, so they are not valid targets.
      ...(!perLocale && toValue(suffix as MaybeRefOrGetter<string>) === '/'
        ? [{ rel: 'alternate' as const, hreflang: 'x-default', href: `${SITE_URL}/` }]
        : [])
    ]
  })

  return () => localeUrl(locale.value, ownSuffix())
}
